"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import {
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  Check,
  FileCheck2,
  Info,
  Mail,
} from "lucide-react";
import type { LegalPageData } from "@/lib/legal";
import { LEGAL_LINKS, LEGAL_SLUG_HREFS } from "@/lib/legal";

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.03 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    },
  };
}

export function LegalDocumentPage({ data }: { data: LegalPageData }) {
  const reduce = useReducedMotion() ?? false;

  return (
    <div className="bg-primary-50/35">
      <motion.div
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.04 }}
        className="mx-auto grid max-w-[1440px] gap-10 px-6 py-12 sm:px-10 sm:py-16 lg:grid-cols-[260px_minmax(0,760px)] lg:justify-between lg:px-20 lg:py-20 xl:grid-cols-[280px_minmax(0,820px)]"
      >
        <motion.aside variants={fadeUp(reduce)} className="lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-card border border-primary-100 bg-white p-5">
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-primary-900">
              <BookOpen className="h-4 w-4 text-accent-500" aria-hidden="true" />
              On this page
            </p>
            <nav className="mt-4" aria-label={`${data.title} sections`}>
              <ul className="space-y-1">
                {data.sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="block rounded-card-sm px-3 py-2 text-sm leading-snug text-text-muted transition-colors hover:bg-primary-50 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </motion.aside>

        <motion.main variants={fadeUp(reduce)} className="min-w-0">
          <header className="mb-8 border-b border-primary-100 pb-8" aria-labelledby="legal-page-heading">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
              <FileCheck2 className="h-4 w-4 text-accent-500" aria-hidden="true" />
              {data.eyebrow}
            </p>
            <div className="mt-3 flex items-start justify-between gap-6">
              <div>
                <h1
                  id="legal-page-heading"
                  className="max-w-[760px] text-[clamp(2.2rem,5vw,4rem)] font-semibold leading-[1.04] tracking-[-0.02em] text-primary-900"
                >
                  {data.title}
                </h1>
                <p className="mt-4 max-w-[680px] text-lg leading-relaxed text-text-body">
                  {data.description}
                </p>
                <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary-100 bg-white px-4 py-2 text-sm font-medium text-primary-700">
                  <CalendarDays className="h-4 w-4 text-accent-500" aria-hidden="true" />
                  {data.effectiveLabel}
                </p>
              </div>

              {data.heroImage && (
                <div className="relative hidden aspect-square w-[150px] shrink-0 sm:block lg:w-[190px]">
                  <Image
                    src={data.heroImage.src}
                    alt={data.heroImage.alt}
                    fill
                    sizes="190px"
                    className="object-contain"
                  />
                </div>
              )}
            </div>
          </header>

          {data.notice && (
            <div className="mb-8 flex gap-3 rounded-card border border-accent-200 bg-accent-50 p-5">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-accent-700" aria-hidden="true" />
              <div>
                <p className="text-sm leading-relaxed text-primary-900">{data.notice}</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                  {data.certificateUrl && (
                    <a
                      href={data.certificateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 underline decoration-primary-200 underline-offset-4 transition-colors hover:text-primary-900"
                    >
                      <FileCheck2 className="h-4 w-4" aria-hidden="true" />
                      {data.certificateLabel ?? "Download certificate"}
                    </a>
                  )}
                  {data.sourceUrl && (
                    <a
                      href={data.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 underline decoration-primary-200 underline-offset-4 transition-colors hover:text-primary-900"
                    >
                      {data.sourceLabel ?? "View official source"}
                      <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {data.badges && (
            <div className="mb-10 grid gap-4 sm:grid-cols-2">
              {data.badges.map((badge) => (
                <div
                  key={badge.label}
                  className="flex items-center gap-4 rounded-card border border-primary-100 bg-white p-5"
                >
                  <div className="relative h-16 w-16 shrink-0">
                    <Image
                      src={badge.src}
                      alt={badge.alt}
                      fill
                      sizes="64px"
                      className="object-contain"
                    />
                  </div>
                  <div>
                    <p className="font-semibold text-primary-900">{badge.label}</p>
                    <p className="mt-1 text-sm leading-relaxed text-text-body">{badge.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <article className="rounded-card border border-primary-100 bg-white px-6 py-2 shadow-sm sm:px-9">
            {data.sections.map((section, index) => (
              <section
                key={section.id}
                id={section.id}
                className={`scroll-mt-28 py-8 sm:py-10 ${
                  index > 0 ? "border-t border-primary-100" : ""
                }`}
                aria-labelledby={`${section.id}-heading`}
              >
                <h2
                  id={`${section.id}-heading`}
                  className="text-[clamp(1.35rem,2.5vw,1.75rem)] font-semibold leading-tight text-primary-900"
                >
                  {section.title}
                </h2>
                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph} className="mt-4 max-w-[70ch] text-base leading-[1.75] text-text-body">
                    {paragraph}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="mt-5 space-y-3">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex max-w-[70ch] gap-3 text-base leading-relaxed text-text-body">
                        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                          <Check className="h-3 w-3" strokeWidth={2.4} aria-hidden="true" />
                        </span>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </article>

          <div className="mt-8 flex flex-col gap-5 rounded-card bg-primary-800 p-6 text-white sm:flex-row sm:items-center sm:justify-between sm:p-7">
            <div>
              <h2 className="text-xl font-semibold text-white">Need clarification?</h2>
              <p className="mt-2 text-sm leading-relaxed text-primary-200">
                Contact the JustGold team for the current document or credential.
              </p>
            </div>
            <Link
              href="/contact-us"
              className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-card-sm bg-white px-5 py-3 text-sm font-semibold text-primary-900 transition-colors hover:bg-accent-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              Contact support
            </Link>
          </div>

          <nav className="mt-10 border-t border-primary-100 pt-8" aria-label="Other legal pages">
            <p className="text-sm font-semibold text-primary-900">Other legal information</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {LEGAL_LINKS.filter((link) => link.href !== LEGAL_SLUG_HREFS[data.slug]).map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-full border border-primary-100 bg-white px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:border-primary-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        </motion.main>
      </motion.div>
    </div>
  );
}
